import { ContentCopyRounded, DeleteRounded, EditRounded } from '@mui/icons-material'
import { Box, Button, Card, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import Color from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'
import { RichTextReadOnly } from 'mui-tiptap'
import { IReportSection, IReportTableColumn, IReportTableRow, ISectionColumn, ReportSectionType, SectionColumnSettingsMap, SectionColumnType } from '../../definitions/report_builder/ReportBuilderDefinitions'
import Helper from '../../helpers/Helper'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { COMPILED_CONSTANTS } from '../../CompiledConstants'
import Highlight from '@tiptap/extension-highlight'

interface ColumnPreviewProps {
    section: IReportSection
    column: ISectionColumn
}

const ColumnRenderer = ({ column }: ColumnPreviewProps) => {
    const theme = useTheme()
    const [imageUrl, setImageUrl] = useState<string | null>(null)
    const [imageLoading, setImageLoading] = useState(false)

    useEffect(() => {
        if (column.type === 'image') {
            const settings = column.settings as SectionColumnSettingsMap['image']
            if (settings?.fileKey) {
                setImageLoading(true)
                ApiClient.readMediaItem(settings.fileKey, COMPILED_CONSTANTS.evolvemediaBucketName)
                    .then(response => {
                        setImageUrl(response.url)
                    })
                    .catch(error => {
                        console.error('Failed to load image:', error)
                        setImageUrl(null)
                    })
                    .finally(() => {
                        setImageLoading(false)
                    })
            } else {
                setImageUrl(null)
            }
        }
    }, [column])

    const renderContent = () => {
        switch (column.type) {
            case 'text': {
                const settings = column.settings as SectionColumnSettingsMap['text']
                return (
                    <div style={{
                        height: 200,
                        overflow: 'auto',
                        padding: 16,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 4
                    }}>
                        <RichTextReadOnly
                            content={settings?.content || 'Text content will appear here'}
                            extensions={[
                                StarterKit,
                                Color,
                                TextStyle,
                                Highlight.configure({
                                    multicolor: true
                                }),
                                Link.configure({
                                    openOnClick: true,
                                    HTMLAttributes: {
                                        style: `
                                            color: ${theme.palette.primary.main};
                                            text-decoration: underline;
                                            cursor: pointer;
                                        `
                                    }
                                })
                            ]}
                        />
                    </div>
                )
            }
            case 'image': {
                const settings = column.settings as SectionColumnSettingsMap['image']
                return (
                    <Box sx={{
                        height: (settings.height && settings.height) > 0 ? settings.height : 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: settings.alignment === 'left' ? 'flex-start' : (settings.alignment === 'right' ? 'flex-end' : 'center'),
                        backgroundColor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden'
                    }}>
                        {imageLoading
                            ? (
                                    <Typography color='text.secondary'>
                                        Loading image...
                                    </Typography>
                                )
                            : imageUrl
                                ? (
                                        <img
                                            src={imageUrl}
                                            alt='Preview'
                                            style={{
                                                maxHeight: '100%',
                                                maxWidth: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    )
                                : settings?.fileKey
                                    ? (
                                            <Typography color='text.secondary'>
                                                Failed to load image
                                            </Typography>
                                        )
                                    : (
                                            <Typography color='text.secondary'>
                                                No image selected
                                            </Typography>
                                        )}
                    </Box>
                )
            }
            case 'table': {
                const settings = column.settings as SectionColumnSettingsMap['table']
                const tableColumns = settings?.columns ?? []
                const tableRows = settings?.rows ?? []

                const getCellStyle = (row: IReportTableRow, col: IReportTableColumn) => {
                    return {
                        ...(col.cellStyle ?? {}),
                        ...(row.rowStyle ?? {}),
                        ...(row.cellStyles?.[col.key] || {})
                    }
                }

                const columns: GridColDef[] = tableColumns.map((col, index) => ({
                    field: `col${index}`,
                    headerName: col.label,
                    flex: 1,
                    width: col.width,
                    renderHeader: () => (
                        <Box
                            sx={{
                                fontWeight: col.headerStyle?.bold ? 'bold' : 'normal',
                                color: col.headerStyle?.color ?? 'inherit',
                                textDecoration: col.headerStyle?.strike ? 'line-through' : 'none',
                                backgroundColor: col.headerStyle?.background ?? 'transparent',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                px: 1
                            }}>
                            {col.label}
                        </Box>
                    ),
                    renderCell: (params: GridRenderCellParams) => {
                        const rowIndex = params.row.rowIndex
                        const row = tableRows[rowIndex]

                        if (row?.isSpacer) {
                            return (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1
                                    }}>
                                </Box>
                            )
                        }

                        const cellStyle = getCellStyle(row, col)
                        // In dark and light mode the underlined text should appear black or white
                        const backgroundType = cellStyle.background && cellStyle.background !== 'transparent' ? Helper.getColorLuminance(cellStyle.background) : null
                        const textColor = backgroundType ? (backgroundType === 'light' ? '#000000' : '#FFFFFF') : cellStyle.color ?? 'inherit'

                        return (
                            <Box
                                sx={{
                                    fontWeight: cellStyle.bold ? 'bold' : 'normal',
                                    color: textColor,
                                    textDecoration: cellStyle.strike ? 'line-through' : 'none',
                                    backgroundColor: cellStyle.background ?? 'transparent',
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1
                                }}>
                                {params.value}
                            </Box>
                        )
                    }
                }))

                const rows = tableRows.map((row, i) => {
                    const rowData: Record<string, any> = {
                        id: i,
                        rowIndex: i
                    }

                    if (row.isSpacer) {
                        tableColumns.forEach((col, colIdx) => {
                            rowData[`col${colIdx}`] = ''
                        })
                    } else {
                        tableColumns.forEach((col, colIdx) => {
                            rowData[`col${colIdx}`] = row.data?.[col.key] ?? `{${row.value}.${col.key}}`
                        })
                    }

                    return rowData
                })

                return (
                    <Box sx={{ height: 200, overflow: 'auto' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            density='compact'
                            hideFooter
                            disableRowSelectionOnClick
                            getRowClassName={params => {
                                const row = tableRows[params.row.rowIndex]
                                return row?.isSpacer ? 'spacer-row' : 'data-row'
                            }}
                            sx={{
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#e6e6e6' // Default header background
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    padding: 0
                                },
                                '& .MuiDataGrid-cell': {
                                    padding: 0
                                },
                                '& .spacer-row .MuiDataGrid-cell': {
                                    minHeight: '20px !important'
                                }
                            }}
                        />
                    </Box>
                )
            }
            case 'chart': {
                return (
                    <Box sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'background.default',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                    }}>
                        <Typography color='text.secondary'>Chart preview</Typography>
                    </Box>
                )
            }
            case 'spacer': {
                return (
                    <Box sx={{
                        height: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'background.default',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1
                    }}>
                    </Box>
                )
            }
            default:
                return null
        }
    }

    return (
        <Box sx={{ flex: 1, p: 1, minHeight: 100 }}>
            {renderContent()}
        </Box>
    )
}

interface P {
    section: IReportSection
    index: number
    handleSelectSection: (section: IReportSection) => void
    handleSelectLayout: (section: IReportSection, value: ReportSectionType) => void
    handleDeleteSection: (section: IReportSection) => void
    handleCloneSection: (section: IReportSection) => void
    handleSelectColumnType: (section: IReportSection, column: ISectionColumn, value: SectionColumnType) => void
}

const ReportSectionRenderer = ({ section, handleSelectSection, handleCloneSection, handleDeleteSection }: P) => {
    const theme = useTheme()

    return (
        <Card sx={{ 'minHeight': 100, 'padding': 1, 'position': 'relative', '&:hover': { '.report-section-commands': { opacity: 1 } }, 'border': 'none' }}>
            <Stack spacing={2}>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        '& > *': {
                            flex: 1,
                            minWidth: 0
                        }
                    }}>
                    {section.columns.map(col => (
                        <ColumnRenderer
                            key={col.uuid}
                            section={section}
                            column={col}
                        />
                    ))}
                </Stack>
            </Stack>
            <Box
                padding={1}
                paddingLeft={2}
                paddingRight={2}
                borderRadius={2}
                sx={{ backgroundColor: theme.palette.background.paper }}
                className='report-section-commands'>
                <Stack direction='row' spacing={1}>
                    <Tooltip arrow title='Edit section'>
                        <IconButton size='small' onClick={() => handleSelectSection(section)}>
                            <EditRounded />
                        </IconButton>
                    </Tooltip>
                    <Tooltip arrow title='Clone section'>
                        <IconButton size='small' onClick={() => handleCloneSection(section)}>
                            <ContentCopyRounded />
                        </IconButton>
                    </Tooltip>
                    <Button
                        color='error'
                        size='small'
                        variant='outlined'
                        onClick={() => handleDeleteSection(section)}
                        startIcon={<DeleteRounded />}>
                        Delete
                    </Button>
                </Stack>
            </Box>
        </Card>
    )
}

export default ReportSectionRenderer
